import Icon from "@/v2-ui/icon"
import Button from "@/v2-backoffice-client/components/button"
import Dot from "@/v2-backoffice-client/components/dot/Dot"
import { CONFIDENCE_DOT_VARIANT } from "@/v2-backoffice-client/translation/translation.constants"
import {
  hasSuggestion,
  isSavable,
  type LangEdit,
} from "@/v2-backoffice-client/translation/editor/translation.editor.utils"

export type TranslationEditorLanguageAiStatusProps = {
  edit: LangEdit
  // A suggest run has completed for this row.
  hasGenerated: boolean
  aiLoading: boolean
  onApplySuggestion(): void
}

// The AI feedback line under a language row's inputs. ALWAYS rendered (empty
// when idle): reserving the line keeps every row the same height across
// idle -> loading -> suggestion states, so the editor never jumps as content
// appears.
function TranslationEditorLanguageAiStatus(
  props: TranslationEditorLanguageAiStatusProps,
) {
  const { edit, hasGenerated, aiLoading, onApplySuggestion } = props

  if (aiLoading) {
    return (
      <>
        <Icon loading size={12} className="shrink-0 text-muted-foreground" />
        <span className="text-muted-foreground">Generating suggestion…</span>
      </>
    )
  }

  const suggestionShown = hasGenerated && isSavable(edit)
  if (suggestionShown) {
    // A shown suggestion the critic judged no better: not an error, the
    // current text is already as good — say so plainly.
    const noImprovement = !edit.isImprovement
    return (
      <>
        <Dot
          variant={CONFIDENCE_DOT_VARIANT[edit.aiConfidence] ?? "muted"}
          className="shrink-0"
          title={
            `Confidence: ${edit.aiConfidence} — the model's own estimate, not a calibrated probability.` +
            (edit.aiNote ? ` Reason: ${edit.aiNote}.` : "")
          }
        />
        <span
          className="min-w-0 truncate"
          title={`${edit.aiName}${edit.aiDesc ? ` — ${edit.aiDesc}` : ""}`}
        >
          <span className="text-muted-foreground">AI:</span> {edit.aiName}
          {edit.aiDesc ? (
            <span className="text-muted-foreground"> — {edit.aiDesc}</span>
          ) : null}
        </span>
        {noImprovement ? (
          <span
            className="shrink-0 cursor-help rounded bg-muted px-1.5 text-[11px] text-muted-foreground"
            title={
              "The critic judged this no clearer or more correct than the current text" +
              (edit.evalNote ? ` — note: ${edit.evalNote}.` : ".")
            }
          >
            no improvement
          </span>
        ) : null}
        <Button
          size="tiny"
          variant="secondary"
          className="shrink-0"
          data-feature-code="UI_BackofficeTranslation_Editor_ApplySuggestion"
          onClick={onApplySuggestion}
          title="Copy this suggestion into the Name/Description fields (nothing is written until Save)."
        >
          Apply
        </Button>
      </>
    )
  }

  if (hasGenerated && hasSuggestion(edit) && !edit.isConsistent) {
    return (
      <span className="text-muted-foreground">
        AI suggestion hidden — critic flagged it inconsistent with the source
        meaning{edit.evalNote ? `: ${edit.evalNote}` : ""}.
      </span>
    )
  }
  if (hasGenerated && !hasSuggestion(edit)) {
    return (
      <span className="text-muted-foreground">
        AI: no improvement found — current text kept.
      </span>
    )
  }
  return null
}

export default TranslationEditorLanguageAiStatus
